import { useQuery } from '@tanstack/react-query';
import { When } from '@teamsparta/react';
import { Flex } from '@teamsparta/stack-flex';
import { InfoCircle } from '@teamsparta/stack-icons';
import { Text } from '@teamsparta/stack-text';
import { vars } from '@teamsparta/stack-tokens';
import { useState } from 'react';

import type { CommunityPostSubComment } from '@/api/community/comment/comment.dto';
import { Dot } from '@/components/common/Dot';
import { useToken } from '@/hooks';
import { profileQuery } from '@/hooks/react-query/community/profile';
import { adjustDateByPurpose, getTimePastText } from '@/lib/utils/date';
import { Actions, LikeButton, ProfileImage } from '@/pages/community/shared';

import { EditSubCommentForm } from '../EditSubCommentForm';
import { useDeleteSubComment, useLikeSubComment } from './logic';

interface Props extends CommunityPostSubComment {
  postId: string;
}

export function SubComment({
  author,
  content,
  isDeleted,
  isMyLike,
  likesCount,
  createdAt,
  postId,
  _id: subCommentId,
}: Props) {
  const { data: profile } = useQuery(profileQuery);
  const isAuthor = profile?._id === author._id;
  const isAdmin = profile?.role === 'admin';

  const [editing, setEditing] = useState(false);
  const { handleClickLike } = useLikeSubComment({
    subCommentId,
    postId,
  });
  const { handleDelete, isPending } = useDeleteSubComment({
    subCommentId,
    postId,
  });

  const { token } = useToken();

  return (
    <When
      condition={!editing}
      fallback={
        <EditSubCommentForm
          postId={postId}
          subCommentId={subCommentId}
          defaultContent={content}
          onCancel={() => setEditing(false)}
        />
      }
    >
      <Flex.Column gap={30} fullWidth>
        <Flex.Row gap={10} fullWidth>
          <ProfileImage
            src={author.imageUrl}
            alt={author.nickname}
            width={24}
            height={24}
          />
          <Flex.Column gap={8} fullWidth>
            <Flex.Column fullWidth gap={4}>
              <Flex.Row justify='between' align='center' gap={2} fullWidth>
                <Flex.Row gap={6} align='center'>
                  <Text as='span' font='tag1Sb' color={vars.neutral[50]}>
                    {author.nickname}
                  </Text>
                  <Dot />
                  <Text as='span' font='tag1Sb' color={vars.neutral[70]}>
                    {getTimePastText(adjustDateByPurpose(createdAt, 'display'))}
                  </Text>
                </Flex.Row>
                <When
                  condition={
                    Boolean(token) && !isDeleted && (isAuthor || isAdmin)
                  }
                >
                  <Actions
                    deleteTitle='댓글을 정말 삭제하시겠습니까?'
                    isDeletePending={isPending}
                    onDelete={handleDelete}
                    onEdit={() => setEditing(true)}
                  />
                </When>
              </Flex.Row>
              <When
                condition={!isDeleted}
                fallback={
                  <Flex.Row gap={3} align='center'>
                    <InfoCircle
                      size={16}
                      color={vars.neutral[50]}
                      variant='line'
                    />
                    <Text as='p' font='captionM' color={vars.neutral[50]}>
                      작성자에 의해 삭제된 댓글입니다.
                    </Text>
                  </Flex.Row>
                }
              >
                <Text
                  as='p'
                  font='captionM'
                  color={vars.neutral[10]}
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                >
                  {content}
                </Text>
              </When>
            </Flex.Column>
            <LikeButton
              count={likesCount}
              liked={isMyLike}
              onClick={handleClickLike}
            />
          </Flex.Column>
        </Flex.Row>
      </Flex.Column>
    </When>
  );
}
